import { MileageTransactionType } from '@orcar/common';
import {
  authAtom,
  getPrimaryColor,
  hasToken,
  isMobileAtom,
  JEJURO,
  LoginModal,
  useGetMileageBalance,
  useGetMileageTransactions,
  useRecoil,
} from '@orcar/common-d2c';
import { useState } from 'react';
import styled from 'styled-components';

const FILTERS = [
  { id: 'all', label: '전체' },
  { id: 'earn', label: '적립' },
  { id: 'use', label: '사용' },
] as const;

const TYPE_LABEL: Record<MileageTransactionType, string> = {
  [MileageTransactionType.EARN]: '적립',
  [MileageTransactionType.USE]: '사용',
  [MileageTransactionType.EXPIRE]: '소멸',
  [MileageTransactionType.REFERRAL]: '친구추천 보상',
  [MileageTransactionType.WELCOME]: '웰컴 적립',
  [MileageTransactionType.CANCEL_CLAWBACK]: '취소 회수',
};

const formatDate = (value: string | Date | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}.${day}`;
};

const MileagePage = () => {
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const { recoilValue: user } = useRecoil(authAtom);
  const isLoggedIn = !!user || !!hasToken();

  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('all');
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const { data: balanceData, isLoading: balanceLoading } = useGetMileageBalance(
    {
      enabled: isLoggedIn,
    },
  );
  const { data: transactions, isLoading: txnLoading } =
    useGetMileageTransactions({ enabled: isLoggedIn });

  // 회원 전용 — 비로그인 시 로그인 유도
  if (!isLoggedIn) {
    return (
      <Wrapper isMobile={isMobile}>
        <SectionTitle isMobile={isMobile}>마일리지</SectionTitle>
        <GateCard>
          <p className='t'>로그인하고 마일리지를 확인하세요</p>
          <p className='d'>
            예약 이용완료(반납) 시 결제금액의 1%가 적립됩니다.
          </p>
          <GateButton type='button' onClick={() => setIsLoginOpen(true)}>
            로그인
          </GateButton>
        </GateCard>
        <LoginModal
          platform={isMobile ? 'mobile' : 'desktop'}
          isOpen={isLoginOpen}
          originUrl={import.meta.env.VITE_ORIGIN_URL}
          onClose={() => setIsLoginOpen(false)}
        />
      </Wrapper>
    );
  }

  const balance = balanceData?.balance ?? 0;
  const expiringAmount = balanceData?.expiringAmount ?? 0;
  const txns = transactions ?? [];
  const isLoading = balanceLoading || txnLoading;

  const visible = txns.filter((txn) =>
    filter === 'all'
      ? true
      : filter === 'earn'
        ? txn.amount > 0
        : txn.amount < 0,
  );

  return (
    <Wrapper isMobile={isMobile}>
      <SectionTitle isMobile={isMobile}>마일리지</SectionTitle>

      <BalanceCard>
        <span className='label'>보유 마일리지</span>
        <span className='amount'>
          {balance.toLocaleString()} <small>P</small>
        </span>
        {expiringAmount > 0 && balanceData?.expiringAt && (
          <span className='sub'>
            {expiringAmount.toLocaleString()}P가{' '}
            {formatDate(balanceData.expiringAt)} 소멸 예정
          </span>
        )}
      </BalanceCard>

      <Tabs>
        {FILTERS.map((f) => (
          <Tab
            key={f.id}
            type='button'
            active={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </Tab>
        ))}
      </Tabs>

      {isLoading ? (
        <Empty>불러오는 중…</Empty>
      ) : visible.length === 0 ? (
        <Empty>내역이 없습니다.</Empty>
      ) : (
        <List>
          {visible.map((txn) => (
            <Row key={txn.id}>
              <div>
                <p className='t'>
                  {txn.reason ?? TYPE_LABEL[txn.type] ?? '마일리지'}
                </p>
                <p className='d'>{formatDate(txn.createdAt)}</p>
              </div>
              <Amount plus={txn.amount > 0}>
                {txn.amount > 0 ? '+' : '−'}
                {Math.abs(txn.amount).toLocaleString()}P
              </Amount>
            </Row>
          ))}
        </List>
      )}

      <Notice>
        예약 이용완료(반납) 시 결제금액의 1%가 적립되며, 적립일로부터 12개월간
        사용할 수 있어요.
      </Notice>
    </Wrapper>
  );
};

export default MileagePage;

const Wrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isMobile }) => (isMobile ? '100%' : '792px')};
  padding: ${({ isMobile }) => (isMobile ? '23px 20px 40px' : '0 0 40px')};
`;

const SectionTitle = styled.p<{ isMobile?: boolean }>`
  margin-bottom: 20px;
  ${({ isMobile, theme }) =>
    isMobile ? theme.NEW_FONT.H4 : theme.NEW_FONT.H3};
  font-weight: 700;
`;

const BalanceCard = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px;
  border-radius: 18px;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: linear-gradient(135deg, ${getPrimaryColor(JEJURO)}, #0077c0);

  &::after {
    content: '';
    position: absolute;
    right: -30px;
    top: -30px;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }

  .label {
    ${({ theme }) => theme.FONT.B3};
    opacity: 0.9;
  }
  .amount {
    ${({ theme }) => theme.FONT.H1};
    font-weight: 800;
    letter-spacing: -0.02em;
    small {
      ${({ theme }) => theme.FONT.H4};
      font-weight: 700;
    }
  }
  .sub {
    margin-top: 4px;
    ${({ theme }) => theme.FONT.B3};
    opacity: 0.92;
  }
`;

const GateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  border-radius: 18px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  text-align: center;

  .t {
    margin: 0;
    ${({ theme }) => theme.FONT.B1};
    font-weight: 700;
  }
  .d {
    margin: 0;
    ${({ theme }) => theme.FONT.B3};
    color: ${({ theme }) => theme.COLOR.GREY_02};
    word-break: keep-all;
  }
`;

const GateButton = styled.button`
  margin-top: 12px;
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
  margin: 24px 0 4px;
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: 0 0 10px;
  background: none;
  border: none;
  cursor: pointer;
  ${({ theme }) => theme.FONT.B1};
  color: ${({ active, theme }) =>
    active ? getPrimaryColor(JEJURO) : theme.COLOR.GREY_02};
  font-weight: ${({ active }) => (active ? 800 : 600)};
  border-bottom: 2px solid
    ${({ active }) => (active ? getPrimaryColor(JEJURO) : 'transparent')};
  margin-bottom: -1px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 2px;
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};

  .t {
    margin: 0;
    ${({ theme }) => theme.FONT.B1};
    font-weight: 600;
  }
  .d {
    margin: 3px 0 0;
    ${({ theme }) => theme.FONT.B3};
    color: ${({ theme }) => theme.COLOR.GREY_02};
  }
`;

const Amount = styled.span<{ plus?: boolean }>`
  ${({ theme }) => theme.FONT.B1};
  font-weight: 800;
  white-space: nowrap;
  color: ${({ plus, theme }) => (plus ? '#1aa576' : theme.COLOR.GREY_01)};
`;

const Empty = styled.p`
  padding: 48px 0;
  text-align: center;
  ${({ theme }) => theme.FONT.B1};
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;

const Notice = styled.p`
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  word-break: keep-all;
`;
